import React, { useState, useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Login from './users/Login'; 
import { Typography, Image as AntImage, Button, Drawer } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { Context } from './store/Context';

// Helper function for smooth scrolling (revised for reliability)
const scrollToSection = (sectionId) => {
  // Wait brief moment for component to potentially mount/be ready
  setTimeout(() => {
     const section = document.getElementById(sectionId);
     if (section) {
       const navbarHeight = 64; // Match navbar CSS height
       const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;
       const scrollToPosition = sectionTop - navbarHeight;
       window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
     } else {
        console.warn(`Section with id "${sectionId}" not found for scrolling.`);
     }
  }, 100); // Small delay might help if elements render slightly later
};

const NavLink = ({ to, sectionId, isExternal = false, children, onClick }) => {
  const location = useLocation();

  const handleClick = (e) => {
    if (sectionId && location.pathname === '/') {
      e.preventDefault();
      scrollToSection(sectionId);
      if (onClick) onClick();
    } else if (!isExternal) {
      // For internal links using RouterLink, let it handle navigation
      // but still call onClick if provided (e.g., to close drawer)
      if (onClick) onClick();
    } else {
      // For external links, allow default behavior (target="_blank" handles new tab)
      if (onClick) onClick();
    }
  };

  if (isExternal) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className="nav-item" // Apply styling via CSS
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  // Check if it's a homepage section link
  const isHomeSectionScroll = sectionId && location.pathname === '/';

  if (isHomeSectionScroll) {
    // Render 'a' tag for same-page scroll
    return (
      <a
        href={`#${sectionId}`}
        className="nav-item"
        onClick={handleClick}
      >
        {children}
      </a>
    );
  } else {
    // Render RouterLink for page navigation
    return (
      <RouterLink
        to={to || '/'} // Fallback to '/' if 'to' is undefined
        className="nav-item"
        onClick={handleClick}
      >
        {children}
      </RouterLink>
    );
  }
};


const NavBar = () => {
  const { state } = useContext(Context);
  const { appLogo, appName } = state;
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const showDrawer = () => setIsDrawerVisible(true);
  const closeDrawer = () => setIsDrawerVisible(false);

  const commonNavLinks = (
     <>
          <li><NavLink to="/" sectionId="home-section" onClick={closeDrawer}>Home</NavLink></li>
          <li><NavLink to="/#team-section" sectionId="team-section" onClick={closeDrawer}>Meet the Team</NavLink></li>
          <li><NavLink to="https://shop.fruitofwar.com" isExternal={true} onClick={closeDrawer}>Shop</NavLink></li>
          <li><NavLink to="/#contact-section" sectionId="contact-section" onClick={closeDrawer}>Contact Us</NavLink></li>
     </>
  );

  // Separate Login for layout control
  const loginComponent = <Login />;

  return (
    <header className="navbar-header"> {/* Class provides fixed positioning, bg, etc */}
      <div className="navbar-container"> {/* Class provides max-width, padding */}
        <RouterLink to="/" className="flex items-center gap-2" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); closeDrawer(); }}>
           <AntImage height={40} preview={false} src={appLogo} alt={`${appName} Logo`} className="navbar-logo-img"/> {/* Add class if needed */}
           <Typography.Title level={3} className="logo-text" style={{ margin: 0 }}>
                {appName}
           </Typography.Title>
        </RouterLink>

        {/* Mobile Menu Button */}
        <Button className="md:hidden" type="text" onClick={showDrawer} icon={<MenuOutlined />} aria-label="Open menu"/>

        {/* Desktop Navigation */}
        <nav className="hidden md:block">
          {/* Apply flex, gap etc via CSS to the ul */}
          <ul className="flex items-center gap-6">
            {commonNavLinks}
             {/* Place login at the end for desktop */}
             <li style={{ marginLeft: 'auto' }}>{loginComponent}</li>
          </ul>
        </nav>

        {/* Mobile Drawer */}
        <Drawer
          title={
            <RouterLink to="/" className="flex items-center gap-2" onClick={closeDrawer}>
                <AntImage height={30} preview={false} src={appLogo} alt={`${appName} Logo`} />
                <span className="logo-text" style={{fontSize: '1.1rem'}}> {appName}</span>
            </RouterLink>
          }
          placement="right"
          onClose={closeDrawer}
          open={isDrawerVisible} // Use 'open' for Antd v5+
          closable={true}
          closeIcon={<CloseOutlined />}
          bodyStyle={{ padding: '1rem', display: 'flex', flexDirection: 'column' }} // Use flex for layout
          headerStyle={{ padding: '1rem 1rem 0.5rem 1rem', borderBottom: '1px solid #eee' }}
          width={300} // Control drawer width
        >
          {/* Apply flex, gap etc via CSS to the ul */}
          <ul className="flex flex-col space-y-4" style={{flexGrow: 1}}> {/* Grow to push login down */}
               {commonNavLinks}
          </ul>
          {/* Place login at the bottom */}
          <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                {loginComponent}
          </div>
        </Drawer>
      </div>
    </header>
  );
};

export default NavBar;